import { resetProbeSegmentUrl } from './shakaLoader';

export const VIDEO_CACHE_NAME = 'bifrost-video-segments';
export const VIDEO_CACHE_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes
const VIDEO_CACHE_MAX_ENTRIES = 150;

// _cb=1 is appended by getProxiedVideoUrl for CORS preflight busting but is
// not part of the canonical resource identity. Stripping it ensures that
// prefetch (which uses _cb) and Shaka playback (which does not) share the
// same cache entry.
function normalizeCacheUrl(url: string): string {
	try {
		const parsed = new URL(url);
		parsed.searchParams.delete('_cb');
		return parsed.toString();
	} catch {
		return url;
	}
}

// Known limitation: starts empty on every page load — entries from previous
// sessions are not counted until touched, so the cap is not enforced across reloads.
const cachedKeys = new Set<string>();

// Maximum time to wait for a CacheStorage read. On mobile under disk pressure,
// cache.match() can hang for tens of seconds — fall through to network fetch
// rather than blocking the segment download.
const CACHE_READ_TIMEOUT_MS = 800;

export async function getVideoCache(): Promise<Cache | null> {
	if (!('caches' in globalThis)) return null;
	return Promise.race([
		caches.open(VIDEO_CACHE_NAME),
		new Promise<null>(resolve =>
			setTimeout(() => resolve(null), CACHE_READ_TIMEOUT_MS)
		)
	]);
}

export async function getCachedResponse(url: string): Promise<Response | null> {
	const cache = await getVideoCache();
	if (!cache) return null;

	const key = normalizeCacheUrl(url);

	const cached = await Promise.race([
		cache.match(key),
		new Promise<null>(resolve =>
			setTimeout(() => resolve(null), CACHE_READ_TIMEOUT_MS)
		)
	]);
	if (!cached) return null;

	const cachedAt = cached.headers.get('x-cached-at');
	if (cachedAt && Date.now() - Number(cachedAt) > VIDEO_CACHE_MAX_AGE_MS) {
		cache.delete(key).catch(() => {});
		cachedKeys.delete(key);
		return null;
	}

	return cached;
}

export async function cacheResponse(
	url: string,
	response: Response
): Promise<void> {
	const cache = await getVideoCache();
	if (!cache) return;

	const key = normalizeCacheUrl(url);
	if (cachedKeys.has(key)) return;

	if (cachedKeys.size >= VIDEO_CACHE_MAX_ENTRIES) {
		const evictCount = cachedKeys.size - VIDEO_CACHE_MAX_ENTRIES + 1;
		let n = 0;
		for (const oldest of cachedKeys) {
			if (n++ >= evictCount) break;
			cachedKeys.delete(oldest);
			cache.delete(oldest).catch(() => {});
		}
	}

	const headers = new Headers(response.headers);
	headers.set('x-cached-at', String(Date.now()));

	const body = await response.clone().arrayBuffer();
	const cachedResponse = new Response(body, {
		status: response.status,
		statusText: response.statusText,
		headers
	});

	await cache.put(key, cachedResponse);
	cachedKeys.add(key);
}

export async function clearVideoCache(): Promise<void> {
	if (!('caches' in globalThis)) return;
	cachedKeys.clear();
	// The probe reuses a cached segment URL — reset it so the next probe does
	// a real CDN fetch rather than hitting a URL no longer in cache.
	resetProbeSegmentUrl();
	await caches.delete(VIDEO_CACHE_NAME);
}
